import { useRef, useState, type FormEvent } from "react";
import {
  ATTACHMENT_MIMES,
  formatIntervalOf,
  nextDue,
  OBLIGATION_KIND_TEXT,
  INTAKE_MAX_ITEMS,
  RECORD_RESULTS,
  todayIso,
  type AssetDetail,
  type AssetObligationDto,
  type AssetObligationsResponse,
  type PresignResponse,
  type RecordResult,
} from "@klantpunt/shared";
import { IconPlus, IconTrash } from "../../app/icons";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { putToStorage } from "../../lib/upload";
import { PartyPicker, type Party } from "../directory/PartyPicker";
import { fmtDay } from "./ObligationSection";
import { sendRecord, useAssetObligations } from "./useObligations";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");

/** Certificaat uploaden (bijlage); alleen online. */
export async function uploadCertificate(f: File): Promise<string> {
  const mime = f.type || "application/pdf";
  if (!(ATTACHMENT_MIMES as readonly string[]).includes(mime))
    throw new ApiError(400, "type", `${f.name}: dit bestandstype wordt niet ondersteund`);
  const id = crypto.randomUUID();
  const p = await api<PresignResponse>("/files/presign", {
    method: "POST",
    body: { id, kind: "attachment", mime, size: f.size, name: f.name },
  });
  await putToStorage(p, f);
  await api(`/files/${id}/confirm`, { method: "POST", body: {} });
  return id;
}

/**
 * Keuring, inspectie, controle of onderhoud vastleggen (docs/ASSETS-MJOP.md, stap 5): met één hand op een
 * telefoon, ook offline. Woorden en velden volgen de soort; het certificaat of de werkbon mag later en wordt
 * zonder verbinding overgeslagen.
 */
export function RecordDialog({
  asset,
  buildingId,
  obligation: o,
  onClose,
  onSaved,
}: {
  /** null = bij het gebouw zelf. */
  asset: AssetDetail | null;
  buildingId: string;
  obligation: AssetObligationDto;
  onClose: () => void;
  onSaved: (r: AssetObligationsResponse | null) => void;
}) {
  const toast = useToast();
  const [date, setDate] = useState(todayIso());
  const [result, setResult] = useState<RecordResult>("goedgekeurd");
  // Voorstel: de uitvoerder van de vorige keer, anders de vaste uitvoerder van deze keuring of dit onderhoud.
  const last = o.lastRecord;
  const savedName = last ? last.performedByCompany : o.performer;
  const [performer, setPerformer] = useState<Party>(() =>
    last
      ? {
          companyId: last.performedByCompanyId,
          personId: last.performedByPersonId,
          userId: null,
          text: last.performedByCompanyId ? "" : (last.performedByCompany ?? ""),
        }
      : {
          companyId: o.performerCompanyId,
          personId: o.performerPersonId,
          userId: null,
          text: o.performerCompanyId ? "" : (o.performer ?? ""),
        },
  );
  const [certification, setCertification] = useState(o.lastRecord?.certification ?? "");
  const [notes, setNotes] = useState("");
  const [defects, setDefects] = useState<string[]>([]);
  const [next, setNext] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const kt = OBLIGATION_KIND_TEXT[o.kind];
  const noun = kt.noun.toLowerCase();
  // Opmerkingen en afkeuringen gaan naar de Inbox (issue #151); een arbeidsmiddel wordt alleen gelabeld.
  const inbox =
    Boolean(useAssetObligations(buildingId, asset?.id ?? null).data?.inbox) && asset?.assetKind !== "equipment";
  const withDefects = inbox && result !== "goedgekeurd";
  const computed = date ? nextDue(date, o.intervalMonths, o.intervalWeeks) : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!date) return;
    setBusy(true);
    try {
      let certificateFileId: string | null = null;
      if (file) {
        if (navigator.onLine === false) toast("Geen verbinding: voeg het certificaat later toe");
        else certificateFileId = await uploadCertificate(file);
      }
      const recordId = crypto.randomUUID();
      const r = await sendRecord(buildingId, `${kt.noun} ${o.name} bij ${asset?.code ?? "het gebouw"}`, o.id, {
        id: recordId,
        performedOn: date,
        result,
        performedByCompany: performer.companyId ? null : performer.text,
        performedByCompanyId: performer.companyId,
        performedByPersonId: performer.personId,
        certification: kt.certification ? certification : null,
        notes,
        nextDueOn: next || null,
        certificateFileId,
        defects: withDefects ? defects.map((d) => d.trim()).filter(Boolean) : [],
      });
      if (r.status === "done") {
        const saved = r.data.obligations.flatMap((x) => x.records).find((x) => x.id === recordId);
        toast(
          saved?.intake
            ? `${kt.noun} vastgelegd; melding ${saved.intake.code} staat in de Inbox ter beoordeling`
            : saved?.finding
              ? `${kt.noun} vastgelegd; er is een bevinding aangemaakt`
              : `${kt.noun} vastgelegd`,
        );
        onSaved(r.data);
      } else {
        toast(`${kt.noun} opgeslagen op dit apparaat; wordt verstuurd zodra er verbinding is`);
        onSaved(null);
      }
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={kt.record}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="submit" form="recform" className="btn primary" disabled={busy || !date}>
            Vastleggen
          </button>
        </>
      }
    >
      <form id="recform" className="stack" onSubmit={submit}>
        <p className="small">
          <strong>{o.name}</strong>
          <span className="muted"> · {asset ? `${asset.code} ${asset.name}` : "bij het gebouw"}</span>
        </p>
        <fieldset className="obresult">
          <legend className="label">{o.kind === "onderhoud" || o.kind === "overig" ? "Uitkomst" : "Resultaat"}</legend>
          {RECORD_RESULTS.map((k) => (
            <label key={k} className={`obres res-${k}`}>
              <input type="radio" name="result" value={k} checked={result === k} onChange={() => setResult(k)} />
              <span>{kt.results[k]}</span>
            </label>
          ))}
        </fieldset>
        {(inbox ? result !== "goedgekeurd" : result === "afgekeurd") && (
          <p className="small obwarn" role="note">
            {inbox
              ? "Dit komt als melding in de Inbox. Daar beoordeelt de servicedesk of er een bevinding van komt."
              : `Bij “${kt.results.afgekeurd}” maakt de app een bevinding ${asset ? "op de plek van de asset" : "bij het gebouw"}.`}
          </p>
        )}
        <label className="field">
          <span className="label">Datum {noun} *</span>
          <input type="date" required value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
        </label>
        <div className={kt.certification ? "formgrid" : undefined}>
          <PartyPicker
            id="record-performer"
            value={performer}
            onChange={setPerformer}
            savedName={savedName}
            buildingId={buildingId}
          />
          {kt.certification && (
            <label className="field">
              <span className="label">Certificering</span>
              <input
                value={certification}
                placeholder="bijv. SCIOS Scope 8"
                onChange={(e) => setCertification(e.target.value)}
              />
            </label>
          )}
        </div>
        <label className="field">
          <span className="label">Volgende {noun}</span>
          <input type="date" value={next} onChange={(e) => setNext(e.target.value)} aria-describedby="rec-next" />
          <span id="rec-next" className="hint">
            {computed
              ? `Leeg = ${fmtDay(computed)} (datum + ${formatIntervalOf(o.intervalMonths, o.intervalWeeks)})`
              : "Geen vaste termijn"}
          </span>
        </label>
        <label className="field">
          <span className="label">Opmerkingen</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        {withDefects && (
          <fieldset className="field obdefects">
            <legend className="label">Gebreken</legend>
            <p className="hint">Eén regel per gebrek. Elk gebrek beoordeelt de servicedesk apart in de Inbox.</p>
            {defects.map((d, i) => (
              <div key={i} className="row">
                <input
                  className="grow"
                  value={d}
                  maxLength={500}
                  aria-label={`Gebrek ${i + 1}`}
                  autoFocus={i === defects.length - 1 && !d}
                  onChange={(e) => setDefects(defects.map((x, j) => (j === i ? e.target.value : x)))}
                />
                <button
                  type="button"
                  className="btn ghost small"
                  aria-label={`Gebrek ${i + 1} verwijderen`}
                  onClick={() => setDefects(defects.filter((_, j) => j !== i))}
                >
                  <IconTrash size={18} aria-hidden="true" />
                </button>
              </div>
            ))}
            {defects.length < INTAKE_MAX_ITEMS && (
              <button type="button" className="btn small" onClick={() => setDefects([...defects, ""])}>
                <IconPlus size={18} aria-hidden="true" />
                Gebrek toevoegen
              </button>
            )}
          </fieldset>
        )}
        <div className="field">
          <span className="label">{kt.document} (mag later)</span>
          <div className="row">
            <button type="button" className="btn small" onClick={() => fileRef.current?.click()}>
              {file ? "Ander bestand" : "Bestand kiezen"}
            </button>
            <span className="small muted grow">{file?.name ?? "Geen bestand"}</span>
          </div>
          <input
            ref={fileRef}
            type="file"
            hidden
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            aria-label={`${kt.document} kiezen`}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
      </form>
    </Modal>
  );
}
